import { BrowserQRCodeReader, IScannerControls } from '@zxing/browser';
import { useEffect, useRef, useState } from 'react';

interface QRScannerProps {
  onScan: (data: string) => void;
}

const QRScanner = ({ onScan }: QRScannerProps) => {
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const controlsRef = useRef<IScannerControls | null>(null);

  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        stream.getTracks().forEach((track) => track.stop());
        setHasPermission(true);
      } catch (err) {
        setHasPermission(false);
        console.error('Camera permission error:', err);
      }
    };

    checkPermissions();
  }, []);

  useEffect(() => {
    if (!hasPermission || !videoRef.current) return;

    const codeReader = new BrowserQRCodeReader();
    const videoElement = videoRef.current;

    codeReader
      .decodeFromVideoDevice(undefined, videoElement, (result, error) => {
        if (result) {
          onScan(result.getText());
        }
        if (error) {
          console.error('QR scanning error:', error);
        }
      })
      .then((controls) => {
        controlsRef.current = controls;
      })
      .catch((err) => {
        console.error('Error starting QR scanner:', err);
      });

    return () => {
      if (controlsRef.current) {
        controlsRef.current.stop();
      }
    };
  }, [hasPermission, onScan]);

  if (hasPermission === null) {
    return <div>Checking camera permissions...</div>;
  }

  if (!hasPermission) {
    return <div>Camera permission denied. Please enable camera access to scan QR codes.</div>;
  }

  return (
    <div className="mx-auto w-full max-w-md">
      <video ref={videoRef} className="w-full" style={{ maxHeight: '400px' }} />
    </div>
  );
};

export default QRScanner;
